/**
 * Style MapLibre : fond Protomaps (PMTiles local), estompage du relief
 * (MNT terrarium local), puis les couches du simulateur.
 * Tout est servi depuis public/ : aucune ressource distante au runtime.
 */
import { layers, namedFlavor } from "@protomaps/basemaps";
import type { LayerSpecification, StyleSpecification } from "maplibre-gl";

/** Racine absolue du site : les gabarits {fontstack}/{range} ne doivent pas être encodés. */
const root = (base: string) => new URL(base, location.href).href;

export const ROUTE_SOURCE = "route";
export const STOPS_SOURCE = "stops";
export const GHOSTS_SOURCE = "ghosts";
export const HOVER_SOURCE = "survol";

export function buildStyle(base: string): StyleSpecification {
  const flavor = namedFlavor("light");
  const basemap = layers("protomaps", flavor, { lang: "fr" }) as LayerSpecification[];

  const hillshade: LayerSpecification = {
    id: "relief",
    type: "hillshade",
    source: "dem",
    paint: {
      "hillshade-exaggeration": 0.3,
      "hillshade-shadow-color": "#4a4a48",
      "hillshade-highlight-color": "#ffffff",
      "hillshade-accent-color": "#8a8a86",
    },
  };
  // l'estompage se glisse juste après la terre, sous l'eau, les routes et les étiquettes
  const earth = basemap.findIndex((l) => l.id === "earth");
  basemap.splice(earth >= 0 ? earth + 1 : 1, 0, hillshade);

  const simulateur: LayerSpecification[] = [
    {
      // zone de clic large : poser une gare au doigt
      id: "route-hit",
      type: "line",
      source: ROUTE_SOURCE,
      paint: { "line-color": "#000", "line-opacity": 0, "line-width": 28 },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      id: "route-casing",
      type: "line",
      source: ROUTE_SOURCE,
      paint: {
        "line-color": "#ffffff",
        "line-width": ["interpolate", ["linear"], ["get", "v"], 20, 5, 110, 9.5],
        "line-opacity": 0.9,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      // le tracé : épaisseur et couleur = vitesse du train
      id: "route-line",
      type: "line",
      source: ROUTE_SOURCE,
      paint: {
        "line-color": [
          "interpolate",
          ["linear"],
          ["get", "v"],
          15, "#b42318",
          45, "#e07a10",
          75, "#7a5af5",
          110, "#1849c8",
        ],
        "line-width": ["interpolate", ["linear"], ["get", "v"], 20, 2.5, 110, 6.5],
      },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      // gares du dossier, en fantôme : un clic les remet
      id: "ghosts",
      type: "circle",
      source: GHOSTS_SOURCE,
      paint: {
        "circle-radius": 6,
        "circle-color": "#ffffff",
        "circle-opacity": 0.35,
        "circle-stroke-color": "#4a4a48",
        "circle-stroke-width": 1.5,
        "circle-stroke-opacity": 0.5,
      },
    },
    {
      id: "ghosts-label",
      type: "symbol",
      source: GHOSTS_SOURCE,
      minzoom: 11,
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Italic"],
        "text-size": 11,
        "text-offset": [0, 1.1],
        "text-anchor": "top",
        "text-optional": true,
      },
      paint: { "text-color": "#6b6b67", "text-halo-color": "#ffffff", "text-halo-width": 1.2 },
    },
    {
      // point survolé dans un graphique
      id: "survol",
      type: "circle",
      source: HOVER_SOURCE,
      paint: {
        "circle-radius": 7,
        "circle-color": "#ffb703",
        "circle-opacity": 0.9,
        "circle-stroke-color": "#111111",
        "circle-stroke-width": 2,
      },
    },
    {
      id: "stops",
      type: "circle",
      source: STOPS_SOURCE,
      paint: {
        "circle-radius": ["case", ["boolean", ["get", "selected"], false], 10, 7.5],
        "circle-color": ["case", ["boolean", ["get", "selected"], false], "#ffb703", "#ffffff"],
        "circle-stroke-color": "#111111",
        "circle-stroke-width": 2.5,
      },
    },
    {
      id: "stops-label",
      type: "symbol",
      source: STOPS_SOURCE,
      minzoom: 9.5,
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Medium"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 9.5, 11, 13, 14],
        "text-offset": [0, -1.2],
        "text-anchor": "bottom",
        "text-allow-overlap": false,
        "text-optional": true,
      },
      paint: { "text-color": "#111111", "text-halo-color": "#ffffff", "text-halo-width": 1.6 },
    },
  ];

  return {
    version: 8,
    glyphs: `${root(base)}fonts/{fontstack}/{range}.pbf`,
    sprite: `${root(base)}sprites/light`,
    sources: {
      protomaps: {
        type: "vector",
        url: `pmtiles://${root(base)}basemap/reunion.pmtiles`,
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · Protomaps',
      },
      dem: {
        type: "raster-dem",
        url: `pmtiles://${root(base)}terrain/reunion-dem.pmtiles`,
        encoding: "terrarium",
        tileSize: 256,
        maxzoom: 13,
        attribution: "relief Mapzen / AWS",
      },
      [ROUTE_SOURCE]: { type: "geojson", data: { type: "FeatureCollection", features: [] } },
      [GHOSTS_SOURCE]: { type: "geojson", data: { type: "FeatureCollection", features: [] } },
      [STOPS_SOURCE]: { type: "geojson", data: { type: "FeatureCollection", features: [] } },
      [HOVER_SOURCE]: { type: "geojson", data: { type: "FeatureCollection", features: [] } },
    },
    layers: [...basemap, ...simulateur],
  };
}
